/**
 * While mounted, forces the global chat widget's default context to the
 * hackathon agent instead of whatever `build-default-context.ts` resolves.
 * Uses DispatchPageContextDataContext — the same proven dispatch path the
 * segmentation builder's ChatInterfaceNative uses — because editing the
 * shared build-default-context.ts didn't propagate through the prebuilt
 * dist of @trinity/chat-components.
 *
 * Scoping is up to the caller. Mount this inside a component that only
 * renders on /projects/11/* to get the hackathon agent there.
 *
 * When a child screen (e.g. SegmentProfile) dispatches its own segment-
 * scoped context, it overrides this bridge's value — dispatch is
 * last-write-wins. On that child's unmount, it should reset to null and
 * this bridge's effect re-fires to restore the bare hackathon agent.
 */
import { useContext, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { BrandInsightsPageContext, ChatContextItem } from '@trinity/chat';
import { ChatContextType } from '@trinity/chat';
import { DispatchPageContextDataContext } from '@trinity/chat-components/components/Chat/context.js';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier.js';

export function HackathonChatContextBridge(): null {
  const dispatch = useContext(DispatchPageContextDataContext);
  const brandIdentifier = useActiveBrandIdentifier();
  const apiClient = useChatApiClient();

  const { data: agents } = useQuery<ChatContextItem[]>({
    queryKey: ['chat-contexts', brandIdentifier],
    queryFn: () =>
      apiClient.chat
        .getChatContexts({ brandIdentifier: brandIdentifier! })
        .then((res) => res.items),
    enabled: !!brandIdentifier,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!dispatch || !agents?.length) return;
    const hackathonAgent = agents.find((a) =>
      a.agent?.identifier?.startsWith('hackathon-agent')
    );
    if (!hackathonAgent) return;

    const bareContext: BrandInsightsPageContext.Agent = {
      type: ChatContextType.Agent,
      data: hackathonAgent,
      metadata: {},
    };
    dispatch(bareContext);

    return () => {
      dispatch(null as unknown as BrandInsightsPageContext.Item);
    };
  }, [dispatch, agents]);

  return null;
}
